import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface CheckInOutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (comment: string) => void;
  isPending: boolean;
  apiStatus: any;
  workflowName: string | undefined;
  versionNumber: number | undefined;
  username: string | undefined;
  mode: "checkin" | "checkout";
}

export function CheckInOutDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  apiStatus,
  workflowName,
  versionNumber,
  username,
  mode,
}: CheckInOutDialogProps) {
  const [comment, setComment] = useState("");

  function handleSave() {
    onConfirm(comment);
  }

  function handleOpenChange(val: boolean) {
    if (!isPending) {
      setComment("");
      onOpenChange(val);
    }
  }

  function handleClear() {
    if (!isPending) {
      setComment("");
    }
  }

  useEffect(() => {
    if (apiStatus.isSuccess || apiStatus.isError) {
      setComment("")
    }
  }, [apiStatus])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "checkin" ? "Check In Workflow" : "Check Out Workflow"}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md bg-muted px-4 py-3 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Workflow name</span>
            <span className="font-medium">{workflowName ?? "—"}</span>
          </div>
          <hr className="border-border" />
          <div className="flex justify-between">
            <span className="text-muted-foreground">Version</span>
            <span className="font-medium">{versionNumber ?? "—"}</span>
          </div>
          <hr className="border-border" />
          <div className="flex justify-between">
            <span className="text-muted-foreground">Username</span>
            <span className="font-medium">{username ?? "—"}</span>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Comment</label>
          <textarea
            className="w-full min-h-[80px] resize-y rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isPending}
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleClear()}
            disabled={isPending}
          >
            Clear
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isPending}>
            {isPending && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}